import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Manual Danube fulfilment flow (used when auto-accept / driver automation is off).
export const DanubeActions = ({ order, onRefresh }) => {
  const [busy, setBusy] = useState('');
  const [boxes, setBoxes] = useState('');
  const [podUrl, setPodUrl] = useState('');
  const status = order.danube?.danube_status;

  const call = async (key, path, body, msg, patch) => {
    setBusy(key);
    try {
      const r = await axios.post(`${API_BASE_URL}/danube/orders/${order.id}/${path}`, body || {}, authCfg());
      toast.success(r.data?.message || msg);
      onRefresh && onRefresh({ ...order, danube: { ...order.danube, ...(patch || {}) } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-2 pt-1 border-t border-yellow-500/20" data-testid="danube-actions">
      {status === 0 && (
        <Button size="sm" className="bg-wt-gold hover:bg-wt-gold-hover text-black w-full" disabled={busy}
          data-testid="danube-accept-btn"
          onClick={() => call('accept', 'accept', {}, 'Order accepted on Danube', { danube_status: 1 })}>
          {busy === 'accept' ? 'Accepting…' : 'Accept Order (max delivery date)'}
        </Button>
      )}

      {status === 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <input type="number" min="1" placeholder="No. of boxes (optional)" value={boxes}
            onChange={(e) => setBoxes(e.target.value)} data-testid="danube-boxes-input"
            className="flex-1 min-w-[150px] h-9 px-3 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-sm" />
          <Button size="sm" className="bg-wt-gold hover:bg-wt-gold-hover text-black" disabled={busy}
            data-testid="danube-ready-pickup-btn"
            onClick={() => call('pickup', 'ready-to-pickup', boxes ? { no_of_boxes: Number(boxes) } : {}, 'Marked packed & shipped on Danube', { danube_status: 3 })}>
            {busy === 'pickup' ? 'Updating…' : 'Ready to Pickup → Packed & Shipped'}
          </Button>
        </div>
      )}

      {status === 3 && (
        <div className="space-y-2">
          <input type="text" placeholder="POD image URL (optional)" value={podUrl}
            onChange={(e) => setPodUrl(e.target.value)} data-testid="danube-pod-input"
            className="w-full h-9 px-3 rounded-md bg-wt-bg border border-wt-border text-wt-text-primary text-sm" />
          <Button size="sm" className="bg-wt-gold hover:bg-wt-gold-hover text-black w-full" disabled={busy}
            data-testid="danube-set-delivered-btn"
            onClick={() => call('delivered', 'set-delivered', { pod_url: podUrl || null }, 'Marked delivered on Danube', { danube_status: 4 })}>
            {busy === 'delivered' ? 'Updating…' : 'Set Delivered (pushes date + POD)'}
          </Button>
        </div>
      )}

      {(status === 4 || status === 9) && (order.proof_of_delivery_url || order.proof_of_delivery_urls?.length > 0) && !order.danube?.pod_uploaded_at && (
        <div className="space-y-1">
          <Button size="sm" variant="outline" className="w-full border-yellow-500/40 text-yellow-400" disabled={busy}
            data-testid="danube-upload-pod-btn"
            onClick={() => call('pod', 'upload-pod', {}, 'POD uploaded to Danube', { pod_uploaded_at: new Date().toISOString(), pod_upload_error: null })}>
            {busy === 'pod' ? 'Uploading…' : 'Upload POD to Danube'}
          </Button>
          {order.danube?.pod_upload_error && (
            <p className="text-xs text-red-400" data-testid="danube-pod-error">Last attempt failed: {order.danube.pod_upload_error}</p>
          )}
        </div>
      )}

      {(status === 4 || status === 9) && !order.danube?.zoho_invoice_number && (
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white w-full" disabled={busy}
          data-testid="danube-generate-invoice-btn"
          onClick={() => call('invoice', 'generate-invoice', {}, 'Zoho invoice created & submitted to Danube')}>
          {busy === 'invoice' ? 'Generating…' : 'Generate Invoice (Zoho Books → Danube)'}
        </Button>
      )}

      {!order.shopify_order_id && (
        <Button size="sm" variant="outline" className="w-full border-wt-border text-wt-text-primary" disabled={busy}
          data-testid="danube-push-shopify-btn"
          onClick={async () => {
            setBusy('shopify');
            try {
              const r = await axios.post(`${API_BASE_URL}/orders/${order.id}/push-to-shopify`, {}, authCfg());
              toast.success(`Created on Shopify — Danube number stays main`);
              onRefresh && onRefresh({ ...order, shopify_order_id: r.data?.shopify_order_id });
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Shopify push failed');
            } finally {
              setBusy('');
            }
          }}>
          {busy === 'shopify' ? 'Pushing…' : 'Push to Shopify'}
        </Button>
      )}
    </div>
  );
};

export default DanubeActions;
